import React from 'react';
import { useSelector } from 'react-redux';
import { renderVisualization } from '@data-visualization/utilities/ChartUtils';
import { NoResult } from '@pages/NoResult';
import { useInViewTracking } from '@utils/hook/tracking/UseInViewTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { useGeneratePageElementViewParam } from '@utils/page-analytics/model/PageElementViewedTracking';
import { FactRevenue, Filters, PageName, PageType } from '@utils/pageConfig/Types';
import { GrowthTypes } from '../comparison-growth-selector/Types';
import { IRootState } from '@state/reducers/Reducers';
import { OnExportReadyCallback } from '@ecosystem/export-utils';
import { ProductsLeaderboardBarChart } from '@data-visualization/products-leaderboard-bar-chart/ProductsLeaderboardBarChart';
import ProductsSegmentMultipleChart from '@data-visualization/products-segment-mulitple-chart/ProductsSegmentMultipleChart';
import { ColumnHeader, ComponentName, KpiOptions, LeaderboardKpi } from '@models/Products';
import { isEmpty } from 'lodash';
import { ColumnConfig, SortingOptionsType } from '@components/design/migrated/Table';
import { getBarChartDropwdownKpi } from '@data-visualization/products-leaderboard-bar-chart/ProductsChartConfig';
import { isPeriodYTDOrMAT } from '@utils/PeriodUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import ErrorResultMessage from '@pages/ErrorResultMessage';

export interface ProductsSegmentsProps {
  isLoading: boolean;
  topSellingProductsContent: LeaderboardKpi[];
  productSpecificationFilters: Filters[];
  sorting: SortingOptionsType;
  selectedGrowthType: GrowthTypes;
  factRevenue: FactRevenue;
  onExportReady: OnExportReadyCallback;
  isUsingCollection?: boolean;
  isApiError?: boolean;
}

const ProductsSegments: React.FC<ProductsSegmentsProps> = ({
  topSellingProductsContent,
  selectedGrowthType,
  factRevenue,
  isLoading,
  productSpecificationFilters,
  sorting,
  onExportReady,
  isUsingCollection = false,
  isApiError,
}) => {
  const pagesConfig = usePageConfig();

  const pageElementViewParam = useGeneratePageElementViewParam();
  const doubleBarChartPageElementViewParam = () => ({
    ...pageElementViewParam,
    pageType: PageType.RI_PRODUCT,
    pageName: PageName.RI_PRODUCT,
    componentName: ComponentName.PRODUCT_LEADERBOARD_TOP_SELLING_PRODUCTS,
    elementType: ElementType.BAR_CHART,
    elementKpi: selectedKpi?.label as string | undefined,
    elementSort: sorting,
    hasData: !isEmpty(topSellingProductsContent),
    access: pagesConfig.access,
  });

  const { ref: ProductsDoubleBarChartComponent } = useInViewTracking(doubleBarChartPageElementViewParam, {
    threshold: 0.2,
    triggerOnce: true,
  });

  const { period } = useSelector((state: IRootState) => state.mainFiltersProducts);

  const getKpiOptions = (columnConfig: ColumnConfig[]): KpiOptions[] =>
    columnConfig.map(({ headerKey, name }) => ({
      label: name,
      value: headerKey as ColumnHeader,
    }));

  const kpiData = getBarChartDropwdownKpi(productSpecificationFilters, isPeriodYTDOrMAT(period));

  const kpiDropdownOptions = getKpiOptions(kpiData);

  const selectedKpi = kpiDropdownOptions.find((option) => option.value === sorting.headerKey) || kpiDropdownOptions[0];

  return (
    <>
      {renderVisualization({
        isLoading: isLoading,
        fetchedApiData: topSellingProductsContent,
        defaultData: [],
        jsxToRender: (
          <div ref={isUsingCollection ? null : ProductsDoubleBarChartComponent}>
            <ProductsLeaderboardBarChart
              topSellingProductsContent={topSellingProductsContent}
              selectedGrowthType={selectedGrowthType}
              factRevenue={factRevenue}
              selectedKpi={selectedKpi}
              onExportReady={onExportReady}
            />
            <ProductsSegmentMultipleChart
              topSellingProductsContent={topSellingProductsContent}
              selectedGrowthType={selectedGrowthType}
              factRevenue={factRevenue}
              selectedKpi={selectedKpi}
            />
          </div>
        ),
        noResultJsx: <div ref={isUsingCollection ? null : ProductsDoubleBarChartComponent}>{NoResult()}</div>,
        errorMessageJsx: (
          <div ref={isUsingCollection ? null : ProductsDoubleBarChartComponent}>{ErrorResultMessage()}</div>
        ),
        isError: isApiError,
      })}
    </>
  );
};

export default ProductsSegments;
